import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowRight, Radar, Gauge, Star, Instagram, MessageCircle, Globe, Sparkles, Bot,
    BarChart3, CheckCircle2, ShieldCheck, MapPin, Phone, Link2, Facebook, Linkedin,
    ChevronDown, AlertTriangle, Check,
} from "lucide-react";
import SiteLayout from "@/marketing/SiteLayout";
import { STEPS, FEATURES, INTEGRATIONS, STATUS_LABEL, PLANS, FAQ } from "@/marketing/content";
import { BRAND } from "@/lib/brand";

const ICONS = { Radar, Gauge, Star, Instagram, MessageCircle, Globe, Sparkles, Bot, BarChart3 };

const STATUS_STYLE = {
    live: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
    analysis: "border-cyan-500/30 bg-cyan-500/10 text-cyan-200",
    soon: "border-white/10 bg-white/5 text-slate-400",
};

function Section({ id, eyebrow, title, sub, children, className = "" }) {
    return (
        <section id={id} className={`scroll-mt-20 max-w-6xl mx-auto px-4 sm:px-6 pt-24 ${className}`}>
            <div className="max-w-2xl">
                {eyebrow && <div className="label-eyebrow text-indigo-300">{eyebrow}</div>}
                <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-slate-50 tracking-tight">{title}</h2>
                {sub && <p className="mt-4 text-base text-slate-400 leading-relaxed">{sub}</p>}
            </div>
            <div className="mt-10">{children}</div>
        </section>
    );
}

function CheckForm({ testid = "hero-check" }) {
    const nav = useNavigate();
    const [name, setName] = useState("");
    const [city, setCity] = useState("");
    const submit = (e) => {
        e.preventDefault();
        try {
            sessionStorage.setItem("ll_prefill", JSON.stringify({ name: name.trim(), city: city.trim() }));
        } catch {}
        nav("/register");
    };
    return (
        <form
            onSubmit={submit}
            data-testid={testid}
            className="surface glow-indigo rounded-2xl p-2 flex flex-col sm:flex-row gap-2 max-w-xl"
        >
            <label className="sr-only" htmlFor={`${testid}-name`}>Business name</label>
            <input
                id={`${testid}-name`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Business name"
                required
                className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none"
            />
            <label className="sr-only" htmlFor={`${testid}-city`}>City</label>
            <input
                id={`${testid}-city`}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                required
                className="sm:w-36 min-w-0 bg-transparent px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none border-t sm:border-t-0 sm:border-l border-white/10"
            />
            <button
                type="submit"
                className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-semibold px-4 py-2.5 transition-colors"
            >
                Check my business <ArrowRight className="h-4 w-4" />
            </button>
        </form>
    );
}

function HeroMock() {
    const rows = [
        { Icon: MapPin, label: "Google Business Profile", value: "4.7 ★ · 212 reviews", ok: true },
        { Icon: Link2, label: "Website", value: "brightsmile.example", ok: true },
        { Icon: Instagram, label: "Instagram", value: "@brightsmile.dental", ok: true },
        { Icon: Facebook, label: "Facebook", value: "Not found — add it", ok: false },
        { Icon: Linkedin, label: "LinkedIn", value: "Bright Smile Dental", ok: true },
        { Icon: Phone, label: "Phone", value: "Matches on 3 of 4 profiles", warn: true },
    ];
    return (
        <div className="relative" aria-hidden="true">
            <div className="absolute inset-0 sm:-inset-6 bg-[radial-gradient(circle_at_30%_20%,rgba(99,102,241,0.25),transparent_55%),radial-gradient(circle_at_80%_90%,rgba(56,189,248,0.18),transparent_50%)] pointer-events-none" />
            <div className="relative surface rounded-2xl p-5 shadow-2xl">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="label-eyebrow">Example result</div>
                        <div className="mt-1 text-base font-bold text-slate-50">Bright Smile Dental</div>
                        <div className="text-xs text-slate-500">Sample business · Ahmedabad</div>
                    </div>
                    <div className="text-right">
                        <div className="text-3xl font-extrabold text-slate-50">72</div>
                        <div className="text-[10px] text-slate-500">presence score</div>
                    </div>
                </div>
                <div className="mt-4 space-y-2">
                    {rows.map(({ Icon, label, value, ok, warn }) => (
                        <div key={label} className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/5 px-3 py-2">
                            <Icon className="h-4 w-4 text-slate-400 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <div className="text-[11px] text-slate-500">{label}</div>
                                <div className="text-sm text-slate-200 truncate">{value}</div>
                            </div>
                            {warn ? (
                                <AlertTriangle className="h-4 w-4 text-amber-300 shrink-0" />
                            ) : ok ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-300 shrink-0" />
                            ) : (
                                <span className="h-2 w-2 rounded-full bg-rose-400 shrink-0" />
                            )}
                        </div>
                    ))}
                </div>
                <div className="mt-4 rounded-xl border border-indigo-500/25 bg-indigo-500/10 p-3">
                    <div className="flex items-center gap-1.5 label-eyebrow text-indigo-300">
                        <Sparkles className="h-3 w-3" /> Top fix
                    </div>
                    <p className="mt-1 text-sm text-slate-200">
                        Your Instagram bio shows an old phone number. Update it to match Google so
                        customers can reach you.
                    </p>
                </div>
            </div>
        </div>
    );
}

function FaqItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-white/5">
            <button
                className="w-full flex items-center justify-between gap-4 py-4 text-left"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
            >
                <span className="text-sm sm:text-base font-semibold text-slate-100">{q}</span>
                <ChevronDown className={`h-4 w-4 text-slate-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && <p className="pb-4 -mt-1 text-sm text-slate-400 leading-relaxed max-w-3xl">{a}</p>}
        </div>
    );
}

export default function Landing() {
    return (
        <SiteLayout>
            <div className="overflow-x-clip">
            {/* Hero */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center" data-testid="landing-hero">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-slate-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                        AI marketing manager for local businesses
                    </div>
                    <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold text-slate-50 leading-[1.05] tracking-tight">
                        See how your business looks online.{" "}
                        <span className="bg-gradient-to-r from-indigo-400 via-cyan-300 to-emerald-300 bg-clip-text text-transparent">
                            Then fix it.
                        </span>
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl">
                        Enter your business name and city. {BRAND.name} finds your Google listing,
                        website, Instagram, Facebook and LinkedIn, checks every one, and tells you
                        exactly what to fix — then drafts replies to customers in your voice, for
                        you to approve.
                    </p>
                    <div className="mt-8">
                        <CheckForm />
                        <p className="mt-3 text-xs text-slate-500">
                            Free to start. You'll create an account, then we run the check.
                        </p>
                    </div>
                </div>
                <HeroMock />
            </section>

            {/* Proof strip */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-16">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        ["5 platforms", "found from a name + city"],
                        ["1 inbox", "for every reply and fix"],
                        ["3 modes", "Suggest · Approval · Autopilot"],
                        ["0 surprises", "nothing posts without your rules"],
                    ].map(([k, v]) => (
                        <div key={k} className="rounded-2xl border border-white/5 bg-white/[0.02] px-4 py-4">
                            <div className="text-lg font-extrabold text-slate-100">{k}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{v}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* How it works */}
            <Section
                id="how"
                eyebrow="How it works"
                title="From a business name to a to-do list in minutes"
                sub="No forms to fill in, no links to hunt for. You stay in charge of every decision."
            >
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {STEPS.map((s) => (
                        <div key={s.n} className="surface rounded-2xl p-5">
                            <div className="mono text-xs text-indigo-300">{s.n}</div>
                            <h3 className="mt-3 text-lg font-bold text-slate-50">{s.title}</h3>
                            <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.body}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Features */}
            <Section
                id="features"
                eyebrow="Features"
                title="Everything customers see, in one place"
                sub="Built for clinics, restaurants, salons, gyms and every business that lives on reviews and local search."
            >
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {FEATURES.map((f) => {
                        const Icon = ICONS[f.icon] || Sparkles;
                        return (
                            <div key={f.key} className="surface rounded-2xl p-5" data-testid={`feature-${f.key}`}>
                                <div className="h-10 w-10 rounded-xl bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center text-indigo-300">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <h3 className="mt-4 text-base font-bold text-slate-50">{f.title}</h3>
                                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.body}</p>
                            </div>
                        );
                    })}
                </div>
            </Section>

            {/* Control */}
            <Section
                id="control"
                eyebrow="You stay in control"
                title="AI that asks before it acts"
                sub="Choose how much the AI does on its own, area by area. Change it any time."
            >
                <div className="grid md:grid-cols-3 gap-4">
                    {[
                        ["Suggest", "The AI drafts. You copy, edit and post yourself.", "border-sky-500/30 text-sky-200"],
                        ["Approval", "Drafts wait in your approvals inbox. One click to publish, edit or reject.", "border-amber-500/30 text-amber-200"],
                        ["Autopilot", "Routine items go out on their own. Risky ones still come to you.", "border-emerald-500/30 text-emerald-200"],
                    ].map(([t, d, cls]) => (
                        <div key={t} className="surface rounded-2xl p-5">
                            <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{t}</span>
                            <p className="mt-3 text-sm text-slate-300 leading-relaxed">{d}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-4 surface rounded-2xl p-5 flex items-start gap-3">
                    <ShieldCheck className="h-5 w-5 text-emerald-300 shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-300 leading-relaxed">
                        A safety override routes legal threats, injuries, medical and privacy issues to a
                        person, replies are checked against claims you've banned, automatic Instagram moderation
                        is opt-in and hides rather than deletes, and every action is written to an audit log.
                    </p>
                </div>
            </Section>

            {/* Integrations */}
            <Section
                id="integrations"
                eyebrow="Integrations"
                title="Connects to the places your customers already are"
                sub="Live integrations use your own account through the platform's official sign-in. Analysis-only sources are read from public pages."
            >
                <div className="grid sm:grid-cols-2 gap-3">
                    {INTEGRATIONS.map((i) => (
                        <div key={i.name} className="rounded-2xl border border-white/5 bg-white/[0.02] p-4 flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-slate-100">{i.name}</div>
                                <div className="mt-1 text-xs text-slate-400 leading-relaxed">{i.note}</div>
                            </div>
                            <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[i.status]}`}>
                                {STATUS_LABEL[i.status]}
                            </span>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Pricing */}
            <Section
                id="pricing"
                eyebrow="Pricing"
                title="Plans that grow with you"
                sub="Every plan starts with a free presence check. Talk to us for pricing in your currency and for agency volume."
            >
                <div className="grid md:grid-cols-3 gap-4">
                    {PLANS.map((p) => (
                        <div
                            key={p.name}
                            className={`rounded-2xl p-6 flex flex-col ${p.highlight ? "surface glow-indigo" : "surface"}`}
                            data-testid={`plan-${p.name.toLowerCase()}`}
                        >
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold text-slate-50">{p.name}</h3>
                                {p.highlight && (
                                    <span className="rounded-full bg-indigo-500/20 border border-indigo-500/30 px-2 py-0.5 text-[11px] font-semibold text-indigo-200">
                                        Most popular
                                    </span>
                                )}
                            </div>
                            <p className="mt-2 text-sm text-slate-400">{p.blurb}</p>
                            <div className="mt-5 text-3xl font-extrabold text-slate-50">
                                {p.price || "Talk to us"}
                                {p.price && p.period && <span className="text-sm font-medium text-slate-500"> / {p.period}</span>}
                            </div>
                            <ul className="mt-5 space-y-2 text-sm text-slate-300 flex-1">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2">
                                        <Check className="h-4 w-4 text-emerald-300 shrink-0 mt-0.5" /> {f}
                                    </li>
                                ))}
                            </ul>
                            {p.price ? (
                                <Link
                                    to="/register"
                                    className={`mt-6 text-center rounded-xl px-4 py-2.5 text-sm font-semibold ${p.highlight ? "bg-indigo-500 hover:bg-indigo-400 text-white" : "border border-white/10 text-slate-100 hover:bg-white/5"}`}
                                >
                                    Get started
                                </Link>
                            ) : (
                                <a
                                    href={`mailto:${BRAND.email}?subject=${encodeURIComponent(`${BRAND.name} — ${p.name} plan`)}`}
                                    className={`mt-6 text-center rounded-xl px-4 py-2.5 text-sm font-semibold ${p.highlight ? "bg-indigo-500 hover:bg-indigo-400 text-white" : "border border-white/10 text-slate-100 hover:bg-white/5"}`}
                                >
                                    Contact us
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            </Section>

            {/* FAQ */}
            <Section id="faq" eyebrow="FAQ" title="Questions, answered">
                <div className="border-t border-white/5">
                    {FAQ.map((f) => (
                        <FaqItem key={f.q} {...f} />
                    ))}
                </div>
            </Section>

            {/* Final CTA */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-24">
                <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0F1226] via-[#0B0F19] to-[#0B1720] p-8 sm:p-12">
                    <div className="absolute inset-0 opacity-50 [background-image:radial-gradient(circle_at_15%_20%,rgba(99,102,241,0.35),transparent_40%),radial-gradient(circle_at_90%_90%,rgba(16,185,129,0.2),transparent_45%)] pointer-events-none" />
                    <div className="relative">
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-50 tracking-tight max-w-2xl">
                            Find out what customers see when they search for you.
                        </h2>
                        <p className="mt-3 text-slate-400 max-w-xl">
                            It takes a business name and a city. The first check is free.
                        </p>
                        <div className="mt-8">
                            <CheckForm testid="footer-check" />
                        </div>
                    </div>
                </div>
            </section>
            </div>
        </SiteLayout>
    );
}
